import React, { useState, useEffect } from 'react';
import { CertificateService } from '../../lib/services/certificateService';
import { CertificateFormat } from '../../lib/types/EncodingOperation';
import './CertificateDecoder.css';

export const CertificateDecoder: React.FC = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [password, setPassword] = useState('');
  const [detectedFormat, setDetectedFormat] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Auto-decode when format changes to PKCS12, but not on password changes
  useEffect(() => {
    if (input.trim() && detectedFormat === 'PKCS12' && !password) {
      decodeCertificateFromText(input);
    }
  }, [detectedFormat]);

  const formatServiceFormatToDisplay = (format: CertificateFormat | null): string => {
    switch (format) {
      case CertificateFormat.PEM:
        return 'PEM';
      case CertificateFormat.DER:
        return 'DER';
      case CertificateFormat.PKCS12:
        return 'PKCS12';
      case CertificateFormat.PKCS7:
        return 'PKCS7';
      case CertificateFormat.BASE64:
        return 'BASE64';
      default:
        return '';
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      setError('');
      let text;
      if (file.name.toLowerCase().endsWith('.p12') || file.name.toLowerCase().endsWith('.pfx')) {
        const buffer = await file.arrayBuffer();
        text = btoa(String.fromCharCode(...new Uint8Array(buffer)));
      } else {
        text = await file.text();
      }

      // Update the input textarea with file content
      setInput(text);

      // Auto-decode after file upload
      setTimeout(() => {
        decodeCertificateFromText(text);
      }, 100);
    } catch (err) {
      console.error('File upload error:', err);
      setError('Failed to read file');
    }
  };

  const decodeCertificateFromText = async (text: string) => {
    if (!text.trim()) {
      setError('Please provide a certificate');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');

      // Use CertificateService to detect format and decode
      const detected = CertificateService.detectFormat(text.trim());
      setDetectedFormat(formatServiceFormatToDisplay(detected));

      if (!detected) {
        setError('Unable to detect certificate format');
        return;
      }

      // Create configuration for the service
      const config = CertificateService.createConfiguration(
        text.trim(),
        'decode' as any, // CertificateMode.DECODE
        {
          inputFormat: detected,
          password: password || undefined
        }
      );

      // Process certificate using the service
      const serviceResult = await CertificateService.process(config);

      if (serviceResult.status === 'error' || !serviceResult.outputData) {
        setError(serviceResult.errorMessage || 'Failed to decode certificate');
        return;
      }

      // Parse the JSON output from service
      let certificateInfo;
      try {
        certificateInfo = typeof serviceResult.outputData === 'string'
          ? JSON.parse(serviceResult.outputData)
          : serviceResult.outputData;
      } catch (parseError) {
        setError('Failed to parse certificate information');
        return;
      }

      // Transform service result to match component's expected format
      const transformedResult = {
        subject: certificateInfo.subject || 'N/A',
        issuer: certificateInfo.issuer || 'N/A',
        validFrom: certificateInfo.notBefore ? new Date(certificateInfo.notBefore) : new Date(),
        validTo: certificateInfo.notAfter ? new Date(certificateInfo.notAfter) : new Date(),
        serial: certificateInfo.serialNumber || 'N/A',
        version: certificateInfo.version || 'N/A',
        format: formatServiceFormatToDisplay(detected),
        fingerprints: certificateInfo.fingerprint || {
          sha256: 'N/A',
          sha1: 'N/A',
          md5: 'N/A'
        },
        // Handle PKCS12 specific metadata
        ...(detected === CertificateFormat.PKCS12 && serviceResult.metadata?.certificateCount && {
          certificateCount: serviceResult.metadata.certificateCount,
          allCertificates: serviceResult.metadata.allCertificates
        }),
        // Handle ECDSA specific info if present
        ...(certificateInfo.publicKeyAlgorithm && {
          publicKeyAlgorithm: certificateInfo.publicKeyAlgorithm
        }),
        ...(certificateInfo.signatureAlgorithm && {
          signatureAlgorithm: certificateInfo.signatureAlgorithm
        }),
        keySize: certificateInfo.publicKey?.keySize || 'N/A',
        isEcdsa: certificateInfo.publicKey?.algorithm?.toLowerCase().includes('ec') || false
      };

      setResult(transformedResult);

    } catch (err) {
      console.error('Certificate decoding error:', err);
      setError('Failed to decode certificate');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/x-x509-ca-cert' ||
          file.type === 'application/x-pem-file' ||
          file.name.endsWith('.pem') ||
          file.name.endsWith('.crt') ||
          file.name.endsWith('.cer') ||
          file.name.endsWith('.der') ||
          file.name.endsWith('.p12') ||
          file.name.endsWith('.pfx')) {
        handleFileUpload(file);
      } else {
        setError('Please upload a valid certificate file (.pem, .crt, .cer, .der, .p12, .pfx)');
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleClear = () => {
    setInput('');
    setResult(null);
    setError('');
    setDetectedFormat('');
    setPassword('');
    setIsProcessing(false);
  };

  return (
    <div className="encoder-container">
      <h1>Certificate Decoder</h1>
      <p>Decode and analyze SSL/TLS certificates in various formats</p>

      {detectedFormat && (
        <div className="format-display">
          <span className="format-label">Detected Format:</span>
          <span className="format-value" data-testid="detected-format">{detectedFormat}</span>
        </div>
      )}

      {/* Password input for encrypted certificates */}
      {detectedFormat === 'PKCS12' && (
        <div className="password-section">
          <label htmlFor="password-input">Certificate Password (optional):</label>
          <div className="password-input-group">
            <input
              id="password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password if certificate is encrypted"
              className="password-input"
              data-testid="password-input"
              disabled={isProcessing}
            />
            <button
              onClick={() => decodeCertificateFromText(input)}
              className="decode-button"
              data-testid="decode-with-password-button"
              disabled={isProcessing || !input.trim()}
            >
              Decode
            </button>
          </div>
          <small className="password-hint">
            Enter password and click Decode to analyze the certificate
          </small>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="cert-input">Certificate:</label>

        {/* File Upload Area */}
        <div
          className={`file-upload-area ${isDragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input')?.click()}
          data-testid="file-upload-area"
        >
          <div className="file-upload-content">
            <div className="file-upload-icon">📁</div>
            <div className="file-upload-text">
              <p>Drag & drop a certificate file here, or click to browse</p>
              <p className="file-types">Supported formats: .pem, .crt, .cer, .der, .p12, .pfx</p>
            </div>
            <input
              type="file"
              id="file-input"
              accept=".pem,.crt,.cer,.der,.p12,.pfx,application/x-x509-ca-cert,application/x-pem-file,application/x-pkcs12"
              onChange={handleFileInputChange}
              data-testid="file-input"
              className="file-input"
              disabled={isProcessing}
            />
          </div>
        </div>

        <textarea
          id="cert-input"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            // Auto-decode on input change with debouncing
            if (e.target.value.trim()) {
              setTimeout(() => decodeCertificateFromText(e.target.value), 300);
            }
          }}
          placeholder="Or paste your certificate here..."
          className="input-textarea"
          rows={10}
          data-testid="input-text"
          disabled={isProcessing}
        />
      </div>

      <div className="button-container">
        <button
          onClick={handleClear}
          className="clear-button"
          data-testid="clear-button"
          disabled={isProcessing}
        >
          Clear
        </button>
      </div>

      {/* Processing indicator */}
      {isProcessing && (
        <div className="processing-indicator" data-testid="processing-indicator">
          Processing certificate...
        </div>
      )}

      {error && (
        <div className="error-message" data-testid="error-message">
          {error}
        </div>
      )}

      {result && (
        <>
          <div className="result-section" data-testid="certificate-info">
            <h3>Certificate Information</h3>

            <div className="cert-details" data-testid="subject-info">
              <strong>Subject:</strong> {result.subject}
            </div>
            <div className="cert-details" data-testid="issuer-info">
              <strong>Issuer:</strong> {result.issuer}
            </div>
            <div className="cert-details" data-testid="validity-info">
              <strong>Valid From:</strong> {result.validFrom.toLocaleString()}<br/>
              <strong>Valid To:</strong> {result.validTo.toLocaleString()}
            </div>
            <div className="cert-details">
              <strong>Serial Number:</strong> {result.serial}
            </div>
            <div className="cert-details">
              <strong>Version:</strong> {result.version}
            </div>

            {/* PKCS12 specific information */}
            {result.certificateCount && (
              <div className="cert-details">
                <strong>Number of Certificates:</strong> {result.certificateCount}
              </div>
            )}

            {result.fingerprints && (
              <div className="fingerprint-section" data-testid="fingerprint-info">
                <h4>Fingerprints</h4>
                <div className="cert-details" data-testid="sha256-fingerprint">
                  <strong>SHA-256:</strong> {result.fingerprints.sha256}
                </div>
                <div className="cert-details" data-testid="sha1-fingerprint">
                  <strong>SHA-1:</strong> {result.fingerprints.sha1}
                </div>
                <div className="cert-details" data-testid="md5-fingerprint">
                  <strong>MD5:</strong> {result.fingerprints.md5}
                </div>
              </div>
            )}

            {/* ECDSA specific information */}
            {result.isEcdsa && (
              <>
                <div className="cert-details">
                  <strong>Public Key Algorithm:</strong> {result.publicKeyAlgorithm || 'ECDSA'}
                </div>
                <div className="cert-details">
                  <strong>Signature Algorithm:</strong> {result.signatureAlgorithm || 'ECDSA'}
                </div>
                <div className="cert-details">
                  <strong>Note:</strong> This is an ECDSA certificate. Information parsed using enhanced certificate service.
                </div>
                <div className="cert-details">
                  <strong>Key Size:</strong> {result.keySize}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};